/*
Usage: 
  <skio-cart-upsell item='{{ item | json | escape }}' line='{{ forloop.index }}'></skio-cart-upsell>
  <script src="{{ 'skio-cart-upsell.js' | asset_url }}" type="module"></script>
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js'

/** Matches Shapes `fetchConfigDefaults("application/javascript")` in assets/utils.js (module scope has no access to that helper). */
function cartChangeFetchConfig() {
  return {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json;',
      Accept: 'application/javascript',
    },
  };
}

function cartChangeUrl() {
  return window.theme?.routes?.cart_change_url || '/cart/change.js';
}

const skioCartStyles = css`
  .skio-cart-upsell {
    margin-top: 10px;
    width: 100%;
    padding: 7px 5px;
    background: transparent;
    border: 1px solid #5d2113;
    border-radius: 8px;
    cursor: pointer;
    max-width: 300px;
    color: #5d2113;
    font-family: "proxima-soft", sans-serif;
    -webkit-appearance: menulist-button;
    appearance: auto;
    position: relative;
    z-index: 1;
    touch-action: manipulation;
  }

  .skio-cart-upgrade {
    margin-top: 10px;
    background: transparent; 
    color: #5d2113;
    border: 1px solid #5d2113;
    outline: none;
    cursor: pointer;
    padding: 7px 5px;
    width: 100%;
    border-radius: 8px;
    transition: color 0.25s, background-color 0.25s, border-color 0.25s;
    font-family: "proxima-soft", sans-serif;
  }

  .skio-cart-upgrade:hover {
    color: #5d2113;
    border-color: #5d2113; 
    background: transparent;
  }
`;

export class SkioCartUpsell extends LitElement {
  static styles = skioCartStyles;

  static properties = {
    item: { type: Object },
    line: { type: Number },

    product: { type: Object },
    selectedVariant: { type: Object },

    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    moneyFormatter: {},
    currency: { type: String }
  };

  /** Native selects fail to open on mobile when rendered inside Shadow DOM. */
  createRenderRoot() {
    return this;
  }

  constructor() {
    super();

    this.item = null;
    this.line = null;

    this.product = null;
    this.selectedVariant = null;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.currency = 'USD';

    this.moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
    });
  }

  render() {
    if (!this.item || !this.product || !this.selectedVariant) return;

    return this.selectedSellingPlan
      ? this.changeFrequency()
      : this.upgradeButton();
  }

  upgradeButton = () => {
    if (!this.availableSellingPlanGroups.length) return html``;

    return html`
      <button
        class="skio-cart-upgrade"
        type="button"
        @click=${() =>
          this.selectSellingPlan(
            this.availableSellingPlanGroups[0].selling_plans[0].id
          )
        }>
        Upgrade to </br> Subscription & Save
      </button>
    `;
  };

  onSelectInteraction = (e) => {
    e.stopPropagation();
  };

  onSelectChange = (e) => {
    e.stopPropagation();
    this.selectSellingPlan(e.target.value);
  };

  planId(planOrId) {
    const id = planOrId?.id ?? planOrId;
    return id == null || id === '' ? '' : String(id);
  }

  isPlanSelected(plan) {
    return this.planId(plan) === this.planId(this.selectedSellingPlan);
  }

  resolveSelectedPlanFromItem() {
    const id = this.item?.selling_plan_allocation?.selling_plan?.id;
    if (id == null || !this.availableSellingPlanGroups.length) return;

    if (Number(this.selectedSellingPlan?.id) === Number(id)) return;

    for (const group of this.availableSellingPlanGroups) {
      const plan = group.selling_plans.find(
        sellingPlan => Number(sellingPlan.id) === Number(id)
      );

      if (plan) {
        this.selectedSellingPlanGroup = group;
        this.selectedSellingPlan = plan;
        return;
      }
    }
  }

  syncSelectValue() {
    if (!this.selectedSellingPlan) return;

    const select = this.querySelector('select.skio-cart-upsell');
    if (!select) return;

    const value = this.planId(this.selectedSellingPlan);
    if (value && select.value !== value) {
      select.value = value;
    }
  }

  changeFrequency = () => {
    const selectedValue = this.planId(this.selectedSellingPlan);

    return html`
      <select
        class="skio-cart-upsell"
        .value=${selectedValue}
        @change=${this.onSelectChange}
        @click=${this.onSelectInteraction}>

        ${!this.product.requires_selling_plan ? html`
          <optgroup label="One Time Purchase">
            <option value="" ?selected=${!this.selectedSellingPlan}>
              One-time
            </option>
          </optgroup>
        ` : ''}

        ${this.availableSellingPlanGroups.map(group => html`
          <optgroup label="${group.name} Purchase">
            ${group.selling_plans.map(plan => html`
              <option
                value="${this.planId(plan)}"
                ?selected=${this.isPlanSelected(plan)}>
                ${plan.name}
              </option>
            `)}
          </optgroup>
        `)}
      </select>
    `;
  };

  updated(changed) {

    if (changed.has('item') && this.item) {
      this.fetchProduct(this.item.handle);
    }

    if (changed.has('product') && this.product) {

      this.skioSellingPlanGroups =
        (this.product.selling_plan_groups || []).filter(
          group => group.app_id === 'SKIO'
        );

      this.selectedVariant =
        (this.product.variants || []).find(
          variant => variant.id == this.item.variant_id
        );
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {

      const allocations =
        this.selectedVariant.selling_plan_allocations || [];

      this.availableSellingPlanGroups =
        this.skioSellingPlanGroups
          .map(group => {

            const mappedPlans =
              group.selling_plans.filter(plan =>
                allocations.some(a =>
                  Number(a.selling_plan_id) === Number(plan.id)
                )
              );

            if (!mappedPlans.length) return null;

            return {
              ...group,
              selling_plans: mappedPlans,
              selected_selling_plan: mappedPlans[0]
            };
          })
          .filter(Boolean);

      this.resolveSelectedPlanFromItem();
    }

    if (changed.has('item') && this.item && this.availableSellingPlanGroups.length) {
      this.resolveSelectedPlanFromItem();
    }

    if (changed.has('selectedSellingPlan') && this.product) {
      this.updateLineItem();
    }

    this.syncSelectValue();
  }

  updateLineItem() {
    if (!this.item) return;

    if (
      this.item.selling_plan_allocation?.selling_plan?.id ==
      this.selectedSellingPlan?.id
    ) return;

    const body = JSON.stringify({
      line: this.line,
      quantity: this.item.quantity,
      selling_plan: this.selectedSellingPlan
        ? this.selectedSellingPlan.id
        : null,

      sections: this.getCartSectionsParam(),
      sections_url: window.location.pathname
    });

    fetch(cartChangeUrl(), {
      ...cartChangeFetchConfig(),
      body
    })
      .then(res => res.text())
      .then(state => {
        const parsedState = JSON.parse(state);

        if (parsedState.status) {
          this.error('Cart change failed:', parsedState.message || parsedState);
          return;
        }

        document.body.dispatchEvent(
          new CustomEvent('shapes:cart:cartqtychange', {
            bubbles: true,
            detail: {
              response: parsedState,
              key: this.item.key,
              quantity: this.item.quantity,
              previousQuantity: this.item.quantity,
              variantId: this.item.variant_id,
              originalTarget: null
            }
          })
        );
      })
      .catch(console.error);
  }

  selectSellingPlan(id) {

    if (!id) {
      this.selectedSellingPlanGroup = null;
      this.selectedSellingPlan = null;
      return;
    }

    const group =
      this.availableSellingPlanGroups.find(group =>
        group.selling_plans.some(
          plan => Number(plan.id) === Number(id)
        )
      );

    const selling_plan =
      group?.selling_plans.find(
        plan => Number(plan.id) === Number(id)
      );

    if (selling_plan) {
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    }
    else {
      this.error('Could not find selling plan:', id);
    }
  }

  money(price) {
    return this.moneyFormatter.format(price / 100.0);
  }

  /** Section file names Shapes expects on cart AJAX responses (see global.bundle.js cart change). */
  getCartSectionsParam() {
    return 'cart-items,cart-footer,cart-item-count,cart-live-region';
  }

  fetchProduct = async(handle) => {

    let productCache =
      window.sessionStorage.skioCartProductCache
        ? JSON.parse(window.sessionStorage.skioCartProductCache)
        : [];

    let cachedProduct =
      productCache.find(product => product.handle == handle);

    if (cachedProduct) {
      this.product = cachedProduct;
      return;
    }

    await fetch(`/products/${handle}.js`)
      .then(res => res.json())
      .then(response => {

        this.product = response;

        productCache.push(response);

        window.sessionStorage.skioCartProductCache =
          JSON.stringify(productCache);

        this.requestUpdate();
      });
  };

  log(...args) {
    args.unshift('%c[skio cart upsell]', 'color: #8770f2;');
    console.log(...args);
  }

  error(...args) {
    args.unshift('%c[skio cart upsell]', 'color: red;');
    console.error(...args);
  }
}

customElements.define('skio-cart-upsell', SkioCartUpsell);
  